import { ThemeEvents, CartUpdateEvent } from '@theme/events';

import {
  QUICK_ADD_CART_SECTION_ID,
  handleizeLike,
  formatCartItemDisplayName,
} from '@theme/zrzs-quick-add-cart-panel';
import { formatMoney } from '@theme/zrzs-quick-add-lens-flow';
import { getRecentlyViewedHandles } from '@theme/recently-viewed-products';

const SOURCE_ID = 'zrzs-quick-add-cart-recs';

/**
 * Lentes/periféricos (convenção de product_type de cart-products.liquid) não fazem sentido como recomendação avulsa.
 * @param {{ product_type?: string, type?: string }} product
 */
function isEyewear(product) {
  const typeHandle = handleizeLike(product.product_type || product.type);
  return typeHandle !== 'lentes' && typeHandle !== 'perifericos';
}

/**
 * Aba "você pode gostar" — lista resolvida em
 * sections/zrzs-quick-add-cart-cross-sell.liquid. Busca cada handle só
 * 1x e decora cada candidato com o produto cheio, pra buildCard fixar a
 * variante certa.
 * @param {number} maxProducts
 * @returns {Promise<Array<{ product: any, id: number, handle: string, price: number, originalPrice?: number, discountPercentage?: number }>>}
 */
export async function fetchCrossSellProducts(maxProducts) {
  const response = await fetch(
    `${window.location.pathname}?section_id=zrzs-quick-add-cart-cross-sell&limit=${maxProducts}`
  );
  if (!response.ok) return [];

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const raw = doc.querySelector('[data-zqcs-candidates]')?.dataset
    .zqcsCandidates;
  if (!raw) return [];

  /** @type {Array<{ id: number, handle: string, price: number, originalPrice?: number, discountPercentage?: number }>} */
  let candidates = [];
  try {
    candidates = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!candidates.length) return [];

  const uniqueHandles = [...new Set(candidates.map(c => c.handle))];
  const productByHandle = new Map();
  await Promise.all(
    uniqueHandles.map(handle =>
      fetch(`/products/${handle}.js`)
        .then(res => (res.ok ? res.json() : null))
        .then(product => {
          if (product) productByHandle.set(handle, product);
        })
        .catch(() => {})
    )
  );

  return candidates
    .map(candidate => {
      const product = productByHandle.get(candidate.handle);
      return product ? { product, ...candidate } : null;
    })
    .filter(
      /** @returns {entry is { product: any, id: number, handle: string, price: number, originalPrice?: number, discountPercentage?: number }} */
      entry => entry !== null
    );
}

/** Aba "recentes" — handles vêm de assets/recently-viewed-products.js, dados frescos via /products/{handle}.js. */
export async function fetchRecentProducts(maxProducts) {
  const handles = (await getRecentlyViewedHandles()).slice(0, maxProducts);
  const products = await Promise.all(
    handles.map(handle =>
      fetch(`/products/${handle}.js`)
        .then(response => (response.ok ? response.json() : null))
        .catch(() => null)
    )
  );

  return products.filter(Boolean);
}

/**
 * sections/zrzs-quick-add-trigger-payload.liquid via Section Rendering API
 * — única fonte dos metafields que /products/{handle}.js não expõe.
 * @param {string} handle
 * @param {number} variantId
 * @returns {Promise<DOMStringMap | null>}
 */
async function fetchTriggerPayload(handle, variantId) {
  try {
    const response = await fetch(
      `/products/${handle}?variant=${variantId}&section_id=zrzs-quick-add-trigger-payload`
    );
    if (!response.ok) return null;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('[data-zqtp-eligible]');
    return node ? node.dataset : null;
  } catch {
    return null;
  }
}

/**
 * data-gifts vem em pseudo-JSON de aspas simples — mesma normalização de
 * assets/zrzs-quick-add-trigger.js e assets/zrzs-quick-add-pdp.js.
 * @param {string | undefined} raw
 * @returns {any[] | null}
 */
function parseGiftsDataset(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw.replace(/'/g, '"'));
  } catch {
    return null;
  }
}

/** Uma aba do drawer desktop (data-zqcd-recs-panel) — mesmo card, só muda a fonte de produtos. */
export class CartRecsController {
  /**
   * @param {object} options
   * @param {HTMLElement} options.panelRoot
   * @param {() => Promise<any[]>} options.fetchProducts
   * @param {number} [options.maxProducts]
   * @param {boolean} [options.clearOnCartEmpty] só a aba "você pode gostar": mostra a mensagem de carrinho vazio e limpa ao esvaziar com o drawer aberto.
   * @param {(available: boolean) => void} [options.onAvailabilityChange] o drawer usa isso pra decidir qual aba fica selecionável.
   */
  constructor({
    panelRoot,
    fetchProducts,
    maxProducts = 4,
    clearOnCartEmpty = false,
    onAvailabilityChange,
  }) {
    this.panelRoot = panelRoot;
    this.fetchProducts = fetchProducts;
    this.maxProducts = maxProducts;
    this.clearOnCartEmpty = clearOnCartEmpty;
    this.onAvailabilityChange = onAvailabilityChange || (() => {});
    this.list = panelRoot.querySelector('[data-zqcd-recs-list]');
    this.emptyMessage = panelRoot.querySelector('[data-zqcd-recs-empty]');
    this.template = document.getElementById('zqcd-rec-card-template');
    this.skeletonTemplate = document.getElementById(
      'zqcd-rec-skeleton-template'
    );
  }

  connect() {
    if (this.clearOnCartEmpty) {
      document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    }
  }

  disconnect() {
    if (this.clearOnCartEmpty) {
      document.removeEventListener(
        ThemeEvents.cartUpdate,
        this.handleCartUpdate
      );
    }
  }

  /**
   * Único cenário em que a mensagem de carrinho vazio aparece — vazio na
   * abertura só marca a aba indisponível, sem mensagem (ver load()).
   * @param {CustomEvent<{ data?: { itemCount?: number } }>} event
   */
  handleCartUpdate = event => {
    if (event.detail?.data?.itemCount !== 0) return;
    if (this.list) this.list.innerHTML = '';
    if (this.emptyMessage) this.emptyMessage.hidden = false;
    this.onAvailabilityChange(false);
  };

  /** @returns {Promise<boolean>} true se achou algo pra mostrar. */
  async load() {
    if (!this.list || !this.template) return false;

    this.renderSkeleton();

    try {
      const products = await this.fetchProducts();
      if (!products.length) return this.renderEmpty();

      this.render(products);
      this.onAvailabilityChange(true);
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CartRecs] Erro ao buscar produtos:', error);
      return this.renderEmpty();
    }
  }

  /** Nada a mostrar na abertura do drawer — sem mensagem (ver handleCartUpdate). @returns {false} */
  renderEmpty() {
    if (this.emptyMessage) this.emptyMessage.hidden = true;
    if (this.list) this.list.innerHTML = '';
    this.onAvailabilityChange(false);
    return false;
  }

  renderSkeleton() {
    if (this.emptyMessage) this.emptyMessage.hidden = true;
    this.list.innerHTML = '';
    if (!this.skeletonTemplate) return;
    for (let i = 0; i < this.maxProducts; i++) {
      this.list.appendChild(this.skeletonTemplate.content.cloneNode(true));
    }
  }

  /** @param {any[]} products */
  render(products) {
    if (this.emptyMessage) this.emptyMessage.hidden = true;
    this.list.innerHTML = '';
    products.forEach(product => {
      const node = this.buildCard(product);
      if (node) this.list.appendChild(node);
    });
  }

  /**
   * Aceita produto puro (aba "recentes") ou candidato decorado do
   * cross-sell (`{ product, id, ... }` — ver fetchCrossSellProducts),
   * com a variante/preço já decididos.
   * @param {any} entry
   */
  buildCard(entry) {
    const product = entry.product || entry;
    const pinnedVariantId = entry.product ? entry.id : null;
    const variant = pinnedVariantId
      ? (product.variants || []).find(v => v.id === pinnedVariantId)
      : (product.variants || []).find(v => v.available) ||
        product.variants?.[0];

    const fragment = /** @type {DocumentFragment} */ (
      this.template.content.cloneNode(true)
    );

    const link = fragment.querySelector('[data-zqcd-rec-link]');
    if (link) link.href = product.url || '#';

    const image = fragment.querySelector('[data-zqcd-rec-image]');
    if (image) {
      image.src = this.imageUrl(product, variant);
      image.alt = product.title || '';
    }

    const name = fragment.querySelector('[data-zqcd-rec-name]');
    if (name) name.textContent = this.displayName(product);

    const price = fragment.querySelector('[data-zqcd-rec-price]');
    if (price) {
      price.textContent = formatMoney(
        entry.product ? entry.price : product.price
      );
    }

    if (entry.discountPercentage) {
      const originalPrice = fragment.querySelector(
        '[data-zqcd-rec-price-original]'
      );
      if (originalPrice) {
        originalPrice.textContent = formatMoney(entry.originalPrice);
        originalPrice.hidden = false;
      }
      const discountTag = fragment.querySelector('[data-zqcd-rec-discount]');
      if (discountTag) {
        discountTag.textContent = `${entry.discountPercentage}% off`;
        discountTag.hidden = false;
      }
    }

    const button = fragment.querySelector('[data-zqcd-rec-add]');
    if (button) {
      if (!variant) {
        button.disabled = true;
      } else {
        button.addEventListener('click', event => {
          // Botão fica dentro do <a> do card (navega pro produto) — barra a navegação pra só adicionar.
          event.preventDefault();
          event.stopPropagation();
          this.handleAdd(product, variant, button);
        });
      }
    }

    return fragment;
  }

  /** Mesma formatação dos itens do carrinho (ex. "lina - grau"). */
  displayName(product) {
    return formatCartItemDisplayName({
      product_title: product.title,
      product_type: product.product_type || product.type,
    });
  }

  /**
   * Prioriza a imagem da variante que handleAdd realmente adiciona —
   * product.featured_image é só a imagem "padrão" do produto, pode ser de
   * outra cor/variante.
   * @param {any} product
   * @param {any} [variant]
   */
  imageUrl(product, variant) {
    const image = variant?.featured_image || product.featured_image;
    if (typeof image === 'string') return image;
    if (image?.src) return image.src;
    return product.images?.[0] || '';
  }

  /**
   * Óculos: tenta abrir o mesmo fluxo completo (confirmação + tamanho +
   * lentes) do card de PLP/PDP — ver openQuickAddFlow. Sem elegibilidade
   * (ou produto não é óculos), cai no add simples de sempre.
   * @param {any} product
   * @param {any} variant
   * @param {HTMLButtonElement} button
   */
  async handleAdd(product, variant, button) {
    // Trava as outras recs pra evitar 2 fetches concorrentes pisando no pendingFrame do overlay.
    const otherButtons = this.list
      ? Array.from(
          this.list.querySelectorAll('[data-zqcd-rec-add]:not(:disabled)')
        ).filter(other => other !== button)
      : [];
    otherButtons.forEach(other => {
      other.disabled = true;
    });
    button.disabled = true;
    try {
      if (
        isEyewear(product) &&
        (await this.openQuickAddFlow(product, variant))
      ) {
        return;
      }
      await this.plainAdd(variant.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CartRecs] Erro ao adicionar:', error);
    } finally {
      button.disabled = false;
      otherButtons.forEach(other => {
        other.disabled = false;
      });
    }
  }

  /**
   * Sem elegibilidade (ex.: óculos de grau sem cesta configurada), devolve
   * false pro chamador cair no add simples.
   * @param {any} product
   * @param {any} variant
   * @returns {Promise<boolean>}
   */
  async openQuickAddFlow(product, variant) {
    const payload = await fetchTriggerPayload(product.handle, variant.id);
    if (!payload || payload.zqtpEligible !== 'true') return false;

    // Fecha o drawer antes de abrir o overlay (mesma coluna, 475px).
    document.dispatchEvent(
      new CustomEvent('zrzs:quick-add:close-cart-desktop')
    );
    // Espera handlePendingOpen terminar antes de resolver (dispatchEvent
    // não espera o listener sozinho).
    await new Promise(resolve => {
      document.dispatchEvent(
        new CustomEvent('zrzs:quick-add:open', {
          detail: {
            pending: true,
            item: {
              image: payload.zqtpImage || '',
              product_title: payload.zqtpProductTitle || '',
              variant_title: payload.zqtpVariantTitle || '',
              price: payload.zqtpPrice ? Number(payload.zqtpPrice) : 0,
              url: payload.zqtpProductUrl || '',
            },
            frameVariantId: payload.zqtpVariantId,
            frameVariantTitle: payload.zqtpVariantTitle || '',
            kitVariantId: payload.zqtpKitVariantId || null,
            hasLensBasket: payload.zqtpHasLensBasket === 'true',
            isSolar: payload.zqtpIsSolar === 'true',
            sizes: payload.zqtpSizes ? JSON.parse(payload.zqtpSizes) : null,
            kitCaseBanner: payload.zqtpKitCaseBanner || null,
            kitCaseDescription: payload.zqtpKitCaseDescription || null,
            giftsData: parseGiftsDataset(payload.zqtpGifts),
            onSettled: resolve,
          },
        })
      );
    });
    return true;
  }

  /**
   * @param {number} variantId
   */
  async plainAdd(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        // HTML do painel do carrinho já atualizado (padrão do tema) — o painel
        // aplica direto do evento, sem buscar de novo.
        sections: QUICK_ADD_CART_SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    const added = await response.json();

    // /cart/add.js devolve só os itens adicionados (sem item_count); o total
    // real sai do JSON do carrinho embutido na section.
    const sectionHtml = added.sections?.[QUICK_ADD_CART_SECTION_ID];
    const cartJson = sectionHtml
      ? new DOMParser()
          .parseFromString(sectionHtml, 'text/html')
          .querySelector('[data-zqao-cart-json]')?.textContent
      : null;
    let cart = added;
    if (cartJson) {
      try {
        cart = JSON.parse(cartJson);
      } catch {
        // JSON da section inválido: o item já entrou — segue com a resposta do /cart/add.js.
      }
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        itemCount: cart.item_count,
        source: SOURCE_ID,
        sections: added.sections,
      })
    );
  }
}
